import { useId, useState } from "react";
import { Button } from "@/components/ui/button";
import { Loader2 } from "lucide-react";
import { useAuth } from "@/_core/hooks/useAuth";
import { openGuestCheckout } from "@/hooks/useGuestCheckout";
import { getLoginUrlForPlan } from "@/const";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { useFreemium } from "@/hooks/useFreemium";
import { useLocale } from "@/hooks/useLocale";
import { Link, useParams } from "wouter";
import { EVENT_SUITE_ID } from "@/lib/workflow/suites";
import { isPremiumApp, MONTHLY_WORKFLOW_IDS } from "@shared/tiers";
import { PREMIUM_APPS, PROFESSIONAL, isWorkflowProduct } from "@/lib/tiers";
import { mobileBuyLinkText } from "@/lib/buyLink";
import { trackCheckoutStart } from "@/lib/analytics";
import { TrialBlockedNote } from "@/components/TrialBlockedNote";
import { USAGE_STRIP_RESERVE_CLASS, USAGE_STRIP_RESERVE_CLASS_PREMIUM } from "@/lib/layoutReserve";

/**
 * The always-visible, three-line usage/upgrade block requested to sit
 * directly under the tool title: today's free-use count, then the two
 * paid plans each with their own "Quick Buy" button. Deliberately
 * replaces UsageBanner (which only appeared once someone was down to
 * their last couple of free uses) rather than sitting alongside it --
 * running both would mean two different "buy unlimited" prompts
 * stacked on the same page once someone got low on free uses.
 *
 * Checkout logic (loading state, redirect to the real Stripe URL,
 * sign-in-first handling) is copied from PricingCards.tsx's own
 * handleUpgrade rather than reinvented -- same billing.createCheckoutSession
 * mutation, same day_pass/monthly plan IDs already wired to real Stripe
 * prices via server/_core/stripe.ts.
 */
export function UsageAndUpgradeStrip() {
  const { isAuthenticated } = useAuth();
  const { id: pageToolId } = useParams<{ id?: string }>();
  const { strings, localizedHref } = useLocale();
  const { remaining, limit, isPro, isAdmin, isLoading } = useFreemium();
  const [loadingPlan, setLoadingPlan] = useState<"day_pass" | "monthly" | null>(null);
  const uid = useId();

  const premium = isPremiumApp(pageToolId);
  const createCheckoutSession = trpc.billing.createCheckoutSession.useMutation();
  const createPremiumCheckout = trpc.premiumApps.checkout.useMutation();

  const handleUpgrade = async (plan: "day_pass" | "monthly") => {
    if (!isAuthenticated) {
      openGuestCheckout(premium ? (plan === "day_pass" ? "premium_day_pass" : "premium_monthly") : plan);
      return;
    }
    setLoadingPlan(plan);
    try {
      const result = premium ? await createPremiumCheckout.mutateAsync({ plan }) : await createCheckoutSession.mutateAsync({ plan });
      trackCheckoutStart(plan, premium ? "premium" : "professional");
      window.location.href = result.url;
    } catch (error) {
      console.error("Failed to start checkout:", error);
      toast.error("Couldn't start checkout. Please try again in a moment.");
      setLoadingPlan(null);
    }
  };

  // Already unlimited (Pro, admin, or an active pass) -- the free
  // counter and "buy unlimited" pitch are both meaningless here, so
  // show nothing rather than confuse someone who's already paid.
  // The Event Suite is a subscription with no trial count and no day pass, so the usual strip does not apply.
  if (pageToolId === EVENT_SUITE_ID) return null;
  // Every Workflow Product has its own pricing (the Book Writing Toolbox is $29.99 a month with a 7-day trial), so the $1 / $4.99 strip never shows on them.
  if (isWorkflowProduct(pageToolId)) return null;
  // Seller Studio and Legal Production Desk are monthly subscriptions behind their own gate.
  if (pageToolId && (MONTHLY_WORKFLOW_IDS as readonly string[]).includes(pageToolId)) return null;
  // While the usage check is on its way, hold the strip's height (an empty box, nothing hidden) so the tool below does
  // not jump down when it arrives. Once the answer is in, an already-unlimited visitor gets nothing, as before.
  const reserve = premium ? USAGE_STRIP_RESERVE_CLASS_PREMIUM : USAGE_STRIP_RESERVE_CLASS;
  if (isLoading) return <div aria-hidden="true" data-testid="usage-strip-reserve" className={`mt-3 ${reserve}`} />;
  if (isPro || isAdmin) return null;

  return (
    <>
    <div className={`mt-3 flex flex-wrap content-start items-center gap-x-5 gap-y-2 ${reserve}`} style={{ color: "var(--signal)" }}>
      <p className="text-sm font-semibold w-full sm:w-auto">
        {strings.usageStrip.freeUsesPerDay.replace("{limit}", String(limit))} ·{" "}
        <span className="text-base font-bold">{strings.usageStrip.remainingToday.replace("{count}", String(remaining))}</span>
      </p>
      {/* Phones: one buy link in place of the two Quick Buy rows (owner-approved; the tier decides the prices). */}
      <Link
        href={localizedHref("/pricing")}
        data-testid="mobile-buy-link"
        className="inline-flex min-h-11 items-center text-sm font-semibold underline sm:hidden"
        style={{ color: "var(--signal)" }}
      >
        {mobileBuyLinkText(premium)}
      </Link>
      <div className="flex items-center gap-2 max-sm:hidden">
        <span id={`${uid}-day`} className="text-xs sm:text-sm font-semibold">{premium ? `Day pass ${PREMIUM_APPS.dayPass}` : strings.usageStrip.dayPassLabel}</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 sm:px-3 text-xs shrink-0"
          style={{ borderColor: "var(--signal)", color: "var(--signal)" }}
          disabled={loadingPlan !== null}
          id={`${uid}-day-btn`}
          aria-labelledby={`${uid}-day-btn ${uid}-day`}
          onClick={() => handleUpgrade("day_pass")}
        >
          {loadingPlan === "day_pass" && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
          {strings.usageStrip.quickBuy}
        </Button>
      </div>
      <div className="flex items-center gap-2 max-sm:hidden">
        <span id={`${uid}-month`} className="text-xs sm:text-sm font-semibold">{premium ? `Monthly ${PREMIUM_APPS.monthly}` : strings.usageStrip.monthlyLabel}</span>
        <Button
          size="sm"
          variant="outline"
          className="h-7 px-2 sm:px-3 text-xs shrink-0"
          style={{ borderColor: "var(--signal)", color: "var(--signal)" }}
          disabled={loadingPlan !== null}
          id={`${uid}-month-btn`}
          aria-labelledby={`${uid}-month-btn ${uid}-month`}
          onClick={() => handleUpgrade("monthly")}
        >
          {loadingPlan === "monthly" && <Loader2 className="h-3 w-3 mr-1.5 animate-spin" />}
          {strings.usageStrip.quickBuy}
        </Button>
      </div>
    </div>
    <TrialBlockedNote toolId={pageToolId} />
    </>
  );
}
